import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import clsx from 'clsx';

import { DEFAULT_AI_LABEL_OPTIONS, SIDE_PANELS, TOOLS } from '../../config';
import { useMediaStudio } from '../../context/MediaStudioContext';
import UploadPanel from '../upload-panel/UploadPanel';
import FormatsPanel from '../formats-panel/FormatsPanel';
import LayersPanel from '../layers-panel/LayersPanel';
import TextPanel from '../text-panel/TextPanel';
import AiLabelsPanel from '../ai-labels-panel/AiLabelsPanel';
import ElementsPanel from '../elements-panel/ElementsPanel';
import InspectorPanel from '../inspector-panel/InspectorPanel';
import useInspectorSections from '../inspector-panel/hooks/useInspectorSections';
import NavTab from './components/nav-tab/NavTab';
import PanelHandle from './components/panel-handle/PanelHandle';
import { INSPECTOR_TAB, PANEL_FEATURES, TAB_META } from './constants';

import scss from './SidePanel.module.scss';

const PANEL_COMPONENTS = {
  [INSPECTOR_TAB]: InspectorPanel,
  [SIDE_PANELS.UPLOAD]: UploadPanel,
  [SIDE_PANELS.TEXT]: TextPanel,
  [SIDE_PANELS.ELEMENTS]: ElementsPanel,
  [SIDE_PANELS.AI_LABELS]: AiLabelsPanel,
  [SIDE_PANELS.FORMATS]: FormatsPanel,
  [SIDE_PANELS.LAYERS]: LayersPanel,
};

const isPanelEnabled = (id, features) => PANEL_FEATURES[id]?.(features) !== false;

function SidePanel({ withInspector = false }) {
  const { t } = useTranslation('mediaStudio');
  const { config, features, editor, layoutMode } = useMediaStudio();
  const hasInspector = useInspectorSections().length > 0;
  const configuredPanels = config.panels.filter((id) => TAB_META[id] && isPanelEnabled(id, features));
  const visiblePanels = withInspector && hasInspector ? [INSPECTOR_TAB, ...configuredPanels] : configuredPanels;
  const [active, setActive] = useState(configuredPanels[0]);
  const [isCollapsed, setIsCollapsed] = useState(false);
  const [aiLabelOptions, setAiLabelOptions] = useState(DEFAULT_AI_LABEL_OPTIONS);
  const isCropMode = editor.tool === TOOLS.CROP;

  useEffect(() => {
    if (isCropMode) {
      setActive(SIDE_PANELS.FORMATS);
      setIsCollapsed(false);
    }
  }, [isCropMode]);

  if (!visiblePanels.length) {
    return null;
  }

  const current = visiblePanels.includes(active) ? active : configuredPanels[0] || visiblePanels[0];
  const Panel = PANEL_COMPONENTS[current];
  const panelProps = {
    [INSPECTOR_TAB]: { isEmbedded: true },
    [SIDE_PANELS.AI_LABELS]: { options: aiLabelOptions, onChangeOptions: setAiLabelOptions },
  };

  const toggleCollapsed = () => setIsCollapsed((value) => !value);

  const selectTab = (id) => {
    if (id === current) {
      toggleCollapsed();
      return;
    }
    setActive(id);
    setIsCollapsed(false);
  };

  return (
    <aside className={clsx(scss.panel, isCollapsed && scss.panelCollapsed)}>
      <div className={scss.nav}>
        <div
          className={scss.tabs}
          role='tablist'
          aria-orientation={layoutMode.isCompact ? 'horizontal' : 'vertical'}>
          {visiblePanels.map((id) => {
            const { icon, labelKey, isComingSoon } = TAB_META[id];
            return (
              <NavTab
                key={id}
                icon={icon}
                label={t(labelKey)}
                isActive={current === id && !isCollapsed}
                isComingSoon={isComingSoon}
                onClick={() => selectTab(id)}
              />
            );
          })}
        </div>
      </div>

      <div className={scss.drawer} aria-hidden={isCollapsed} inert={isCollapsed ? '' : undefined}>
        <div className={scss.drawerInner}>{Panel ? <Panel {...panelProps[current]} /> : null}</div>
      </div>

      <PanelHandle isCollapsed={isCollapsed} onToggle={toggleCollapsed} />
    </aside>
  );
}

export default SidePanel;
